import Lenis from "lenis";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { EnterGate } from "@/components/EnterGate";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FloatingRadio } from "@/components/FloatingRadio";
import { SiteHeader } from "@/components/SiteHeader";
import { Toaster } from "@/components/ui/sonner";
import { googleSession } from "@/utils/auth";
import CharacterBook from "@/pages/CharacterBook";
import Enlist from "@/pages/Enlist";
import Home from "@/pages/Home";
import Game from "@/pages/Game";
import Registry from "@/pages/Registry";
import Riotball from "@/pages/Riotball";
import TeamDossier from "@/pages/TeamDossier";
import DoloPals from "@/pages/DoloPals";
import Rewards from "@/pages/Rewards";
import SideGames from "@/pages/SideGames";
import Champions from "@/pages/Champions";
import Account from "@/pages/Account";
import { DailyRation } from "@/components/DailyRation";
import TcgArena from "@/pages/TcgArena";
import { WeeklyPrize } from "@/components/WeeklyPrize";
import WastelandRadio from "@/pages/WastelandRadio";
import Sponsors from "@/pages/Sponsors";

/* Google OAuth return: exchange the one-time session_id server-side, then
   land on the account dossier with the signed-in user */
function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    googleSession(sessionId)
      .then((user) => navigate("/account", { state: { user }, replace: true }))
      .catch(() => navigate("/account", { replace: true }));
  }, [location, navigate]);

  return null;
}

/* the session_id fragment check must happen synchronously during render —
   useEffect would run too late and race the session exchange */
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/character-book" element={<CharacterBook />} />
      {/* the sand worm dossier now lives inside the character book */}
      <Route path="/sand-worm" element={<Navigate to="/character-book" replace />} />
      {/* the bio hack system now lives at the bottom of the riotball page */}
      <Route path="/bio-hack" element={<Navigate to="/riotball" replace />} />
      <Route path="/riotball" element={<Riotball />} />
      <Route path="/riotball/team/:teamId" element={<TeamDossier />} />
      <Route path="/wasteland-radio" element={<WastelandRadio />} />
      <Route path="/side-games" element={<SideGames />} />
      <Route path="/champions" element={<Champions />} />
      <Route path="/dolopals" element={<DoloPals />} />
      <Route path="/sponsors" element={<Sponsors />} />
      <Route path="/rewards" element={<Rewards />} />
      <Route path="/account" element={<Account />} />
      <Route path="/registry" element={<Registry />} />
      <Route path="/wasteland-run" element={<Game />} />
      <Route path="/tcg" element={<TcgArena />} />
      <Route path="/enlist" element={<Enlist />} />
    </Routes>
  );
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;

    let frameId;
    const raf = (time) => {
      lenis.raf(time);
      frameId = requestAnimationFrame(raf);
    };
    frameId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(frameId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return null;
}

function ScrollManager() {
  const location = useLocation();

  // entering the site (or refreshing mid-scroll) always lands at the top
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.state]);

  return null;
}

function App() {
  const [gatePassed, setGatePassed] = useState(() => sessionStorage.getItem("dolo-gate-access") === "granted");

  // the moment the gate lifts, the site must sit flush at the top
  useEffect(() => {
    if (!gatePassed) return;
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    const frame = window.requestAnimationFrame(() => window.scrollTo(0, 0));
    return () => window.cancelAnimationFrame(frame);
  }, [gatePassed]);

  return (
    <div className="App">
      <BrowserRouter>
        <SmoothScroll />
        <ScrollManager />
        {!gatePassed ? <EnterGate onEnter={() => setGatePassed(true)} /> : null}
        <SiteHeader />
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
        <Toaster position="bottom-right" theme="dark" />
        <FloatingRadio />
        <DailyRation />
        <WeeklyPrize />
      </BrowserRouter>
    </div>
  );
}

export default App;
